import { useCallback } from 'react';

import {
  useMutation as useUnthrottledMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { handleError } from 'api/query-client';

import {
  cancelJourneyReview,
  FindingSignature,
  ignoreFindings,
  JourneyReview,
  loadLatestJourneyReview,
  restoreFindings,
  StartedJourneyReview,
  startJourneyReview,
} from './requests';
import { findingSignature, signatureKey } from './signature';

export const getLatestJourneyReviewQueryKey = (journeyId?: string) =>
  ['journey-review', journeyId, 'latest'].filter(Boolean);

const RUN_POLL_INTERVAL_MS = 3000;

export const useLatestJourneyReview = (journeyId: string, options?: { enabled?: boolean }) => {
  return useQuery<JourneyReview | null>({
    queryKey: getLatestJourneyReviewQueryKey(journeyId),
    queryFn: () => loadLatestJourneyReview(journeyId),
    refetchInterval: (data) => (data?.status === 'in_progress' ? RUN_POLL_INTERVAL_MS : false),
    ...options,
    // After the spread: a caller's `enabled` must narrow the id guard, not replace it.
    enabled: Boolean(journeyId) && (options?.enabled ?? true),
  });
};

export const useInvalidateJourneyReviews = () => {
  const queryClient = useQueryClient();

  const invalidateLatestJourneyReview = useCallback(
    (journeyId: string) => {
      return queryClient.invalidateQueries(getLatestJourneyReviewQueryKey(journeyId));
    },
    [queryClient]
  );

  return { invalidateLatestJourneyReview };
};

export const useStartJourneyReview = (journeyId: string) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { invalidateLatestJourneyReview } = useInvalidateJourneyReviews();

  // Not the throttled useMutation this file aliases: it de-dupes on the variables
  // hash, and with no variables a legitimate retry within 1s would collide.
  return useUnthrottledMutation<StartedJourneyReview, AxiosError, void>({
    mutationFn: () => startJourneyReview(journeyId),
    onSuccess: (started) => {
      queryClient.setQueryData<JourneyReview | null>(
        getLatestJourneyReviewQueryKey(journeyId),
        (previous) => ({
          shortId: started.reviewShortId,
          status: 'in_progress',
          findings: [],
          partial: false,
          partialNote: null,
          error: null,
          dateCreated: previous?.dateCreated ?? new Date().toISOString(),
        })
      );
      invalidateLatestJourneyReview(journeyId);
    },
    onError: (error) =>
      handleError(
        error?.response?.status === 409 ? t('reviewAlreadyRunning') : t('reviewNotStarted')
      ),
  });
};

export const useCancelJourneyReview = (journeyId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateLatestJourneyReview } = useInvalidateJourneyReviews();

  return useUnthrottledMutation<void, AxiosError, void>({
    mutationFn: () => cancelJourneyReview(journeyId),
    onSuccess: () => invalidateLatestJourneyReview(journeyId),
    onError: () => handleError(t('cancelFailed')),
  });
};

// No onError rollback: useThrottledMutation hands it an empty context, so the
// onSettled refetch is what undoes a failed flip.
const useToggleIgnored = (
  journeyId: string,
  nextIgnored: boolean,
  mutationFn: (signatures: FindingSignature[]) => Promise<void>,
  errorMessage: string
) => {
  const queryClient = useQueryClient();
  const { invalidateLatestJourneyReview } = useInvalidateJourneyReviews();
  const queryKey = getLatestJourneyReviewQueryKey(journeyId);

  return useMutation<void, AxiosError, FindingSignature[]>({
    mutationFn,
    onMutate: async (signatures) => {
      await queryClient.cancelQueries(queryKey);
      const keys = new Set(signatures.map(signatureKey));
      queryClient.setQueryData<JourneyReview | null>(queryKey, (review) =>
        review
          ? {
              ...review,
              findings: review.findings.map((finding) =>
                keys.has(signatureKey(findingSignature(finding)))
                  ? { ...finding, ignored: nextIgnored }
                  : finding
              ),
            }
          : review
      );
    },
    onError: () => handleError(errorMessage),
    onSettled: () => invalidateLatestJourneyReview(journeyId),
  });
};

export const useIgnoreFindings = (journeyId: string) => {
  const { t } = useScopedTranslate(locale);
  return useToggleIgnored(
    journeyId,
    true,
    (signatures) => ignoreFindings(journeyId, signatures),
    t('ignoreFailed')
  );
};

export const useRestoreFindings = (journeyId: string) => {
  const { t } = useScopedTranslate(locale);
  return useToggleIgnored(
    journeyId,
    false,
    (signatures) => restoreFindings(journeyId, signatures),
    t('reopenFailed')
  );
};

export const locale = {
  en: {
    reviewNotStarted: 'Could not start the review',
    reviewAlreadyRunning: 'A review of this journey is already running',
    cancelFailed: 'Could not stop the review',
    ignoreFailed: 'Could not ignore this finding',
    reopenFailed: 'Could not reopen this finding',
  },
};
